import { BulkField } from '@/src/constants'
import { CloseIcon, DownloadIcon, UploadIcon } from '@icons'
import UploadCSVImage from '@images/UploadCSV.png'
import { Box, Heading, Text } from 'grommet'
import { ChangeEvent, useRef, useState } from 'react'
import { downloadCsvTemplate } from '../../../utils/helper'
import { DeleteModal } from './DeleteModal'

type Props = {
  headerArray?: BulkField[]
  handleSubmit?: (file?: File) => Promise<unknown> | unknown
  onClose?: () => void
  onDownloadSample?: () => void
  onFileSelect?: (file: File) => void
  isLoading?: boolean
  title?: string
  context?: string
  description?: string
  lBtnLabel?: string
  rBtnLabel?: string
  sampleFileName?: string
  className?: string
}

export const BulkUploadModal = ({
  headerArray,
  handleSubmit,
  onClose,
  onDownloadSample,
  onFileSelect,
  isLoading,
  title,
  context = 'drugs',
  description = 'Ensure the file follows the provided template:',
  lBtnLabel = 'Download CSV Sample',
  rBtnLabel = 'Select CSV file',
  sampleFileName,
  className
}: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const headers = headerArray || []
  const modalTitle = title || `Upload a CSV file containing ${context} Information`

  const handleDownloadSample = () => {
    downloadCsvTemplate(headers, sampleFileName)
    onDownloadSample && onDownloadSample()
  }

  const handleSelectCsvFile = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (!file) return

    onFileSelect && onFileSelect(file)
    setIsUploading(true)
    try {
      if (handleSubmit) {
        await handleSubmit(file)
      }
      onClose && onClose()
    } catch {
      // keep modal open on error; caller should toast via handleSubmit
    } finally {
      setIsUploading(false)
    }
    event.target.value = ''
  }

  const modalHeader = (
    <Box
      align="center"
      border={{ color: '#D9D9D9', side: 'bottom', size: '1px' }}
      direction="row"
      justify="between"
      pad={{ horizontal: '34px', vertical: '15px' }}
      width="100%"
    >
      <Heading color="black" level={6} margin="none" size="20px" weight="400">
        Bulk File Upload
      </Heading>

      <CloseIcon className="cursor-pointer" onClick={onClose} />
    </Box>
  )

  const uploadDescription = (
    <Box pad={{ horizontal: '26px', top: '25px' }} width="100%">
      <Box align="top" direction="row" gap="26px" margin={{ bottom: '16px' }}>
        <Box
          align="center"
          background="#EBFFE4"
          flex={false}
          height="188px"
          justify="center"
          pad="16px"
          round="18px"
          width="236px"
        >
          <img alt="" className="h-full max-h-[102px] w-full max-w-[156px] object-contain" src={UploadCSVImage} />
        </Box>
        <Box flex="grow" gap="small" style={{ minWidth: 0 }}>
          <Heading color="black" level={3} margin="none" size="20px" weight="400">
            {modalTitle}
          </Heading>
          <Text color="#333333" size="14px">
            {description}
          </Text>
          <Box
            as="ul"
            className="list-disc max-h-[min(240px,32vh)] overflow-y-auto"
            gap="small"
            margin={{ bottom: '0', top: 'xsmall' }}
            pad={{ left: '28px', right: '8px' }}
          >
            {headers.map((header, i) => (
              <Text as="li" color="#333333" key={i} size="13px">
                {header.label}
              </Text>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )

  return (
    <>
      <input
        accept=".csv,text/csv"
        // aria-label=""
        className="hidden"
        onChange={handleFileChange}
        ref={fileInputRef}
        title="csv_uploader"
        type="file"
      />
      <DeleteModal
        buttonPad={{ bottom: '40px', horizontal: '32px', top: '0' }}
        className={className}
        description={uploadDescription}
        descriptionWidth="100%"
        gridBtn
        handleSubmit={handleSelectCsvFile}
        hideTitle
        isLoading={isUploading || !!isLoading}
        lBtnLabel={lBtnLabel}
        leftBtnClassName="!h-[55px] !w-full !rounded-full !border-0 !bg-[#F6F5EF] !text-[15px] !font-normal !text-black hover:!bg-[#F6F5EF] hover:!text-black"
        leftIcon={<DownloadIcon />}
        modalHeader={modalHeader}
        noIcon
        onClose={onClose}
        onLeftBtnClick={handleDownloadSample}
        pad="none"
        rBtnLabel={rBtnLabel}
        rightBtnClassName="!h-[55px] !w-full !rounded-full !bg-primary !text-[15px] !font-semibold !text-white"
        rightIcon={<UploadIcon />}
        title="Bulk File Upload"
        width="700px"
      />
    </>
  )
}
